import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldAlert } from "lucide-react";

const WAIVER_TEXT = `SMASH & DASH — RELEASE, WAIVER, AND ASSUMPTION OF RISK AGREEMENT

1. ADULTS ONLY. You represent and warrant that you are at least eighteen (18) years of age. Using this service while under 18 is a violation of these terms and potentially applicable law.

2. THE SERVICE IS A DISCOVERY TOOL, NOT A BACKGROUND-CHECK SERVICE. Smash & Dash does not verify the criminal background, identity, intentions, sexual history, STI status, marital status, employment, or character of any user on the platform. Profile information is self-reported and may be incomplete, outdated, or entirely false.

3. MEETING STRANGERS IS DANGEROUS. You understand that communicating with or meeting any other user — in person, virtually, or through any other medium — involves inherent and serious risks, including but not limited to: physical assault, sexual assault, kidnapping, robbery, exposure to sexually transmitted infections, emotional distress, harassment, stalking, fraud, identity theft, and death.

4. ASSUMPTION OF RISK. You knowingly, freely, and voluntarily assume all such risks, both known and unknown, arising out of or related to your use of Smash & Dash, including any interaction with another user.

5. RELEASE AND WAIVER. You hereby release, waive, discharge, and covenant not to sue Smash & Dash, its owners, operators, officers, directors, employees, agents, affiliates, and licensors (collectively, the "Released Parties") from any and all liability, claims, demands, actions, and causes of action whatsoever arising out of or related to any loss, damage, injury, or death that may be sustained by you, or to any property belonging to you, while using the service or meeting any user.

6. INDEMNIFICATION. You agree to indemnify and hold the Released Parties harmless from any loss, liability, damage, or cost they may incur due to your conduct or your interactions with other users.

7. NO LIABILITY. UNDER NO CIRCUMSTANCES SHALL THE RELEASED PARTIES BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES ARISING FROM YOUR USE OF THE SERVICE OR YOUR INTERACTION WITH ANY USER, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.

8. SAFETY GUIDELINES. You agree to exercise caution and good judgment when meeting users. Always meet in public, tell a friend where you are going, practice safe sex, do not share financial information, and trust your instincts.

9. PROHIBITED CONDUCT. You will not use the service for any illegal activity, including but not limited to prostitution, human trafficking, minors, revenge pornography, harassment, or non-consensual interactions.

10. SEVERABILITY. If any portion of this agreement is held invalid, the remainder shall continue in full legal force and effect.

BY CHECKING THE BOX BELOW AND CLICKING "I ACCEPT," YOU ACKNOWLEDGE THAT YOU HAVE READ THIS AGREEMENT, UNDERSTAND ITS CONTENTS, AND AGREE TO BE BOUND BY ITS TERMS.`;

export default function Waiver() {
    const nav = useNavigate();
    const [accepted, setAccepted] = useState(false);
    const [scrolledEnd, setScrolledEnd] = useState(false);
    const scrollRef = useRef(null);

    const onScroll = () => {
        const el = scrollRef.current;
        if (!el) return;
        const bottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
        if (bottom) setScrolledEnd(true);
    };

    const onContinue = () => {
        // Persist locally for registration
        sessionStorage.setItem("sd_waiver_accepted", "true");
        nav("/signup");
    };

    return (
        <div className="relative min-h-screen flex flex-col px-6 pt-8 pb-6 text-white">
            <button
                onClick={() => nav("/age-gate")}
                data-testid="waiver-back"
                className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-white/50 hover:text-white w-fit mb-4"
            >
                <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>

            <div className="flex items-center gap-2 mb-4">
                <ShieldAlert className="w-5 h-5 text-smash" />
                <span className="text-[10px] uppercase tracking-[0.3em] text-smash">Legal Liability Waiver</span>
            </div>
            <h2 className="font-display text-4xl text-white mb-1">AT YOUR</h2>
            <h2 className="font-display text-4xl text-smash mb-5">OWN RISK.</h2>

            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                ref={scrollRef}
                onScroll={onScroll}
                data-testid="waiver-scroll"
                className="flex-1 overflow-y-auto bg-ink-800 border border-white/10 p-4 text-[11px] text-white/80 leading-relaxed whitespace-pre-wrap font-body"
                style={{ maxHeight: "45vh" }}
            >
                {WAIVER_TEXT}
            </motion.div>

            <div className="mt-4 space-y-3">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="waiver-accept-label">
                    <input
                        type="checkbox"
                        data-testid="waiver-checkbox"
                        checked={accepted}
                        onChange={(e) => setAccepted(e.target.checked)}
                        disabled={!scrolledEnd}
                        className="w-5 h-5 mt-0.5 accent-smash shrink-0 cursor-pointer disabled:opacity-30"
                    />
                    <span className={`text-xs leading-snug ${scrolledEnd ? "text-white/80" : "text-white/30"}`}>
                        {scrolledEnd
                            ? "I have read the entire agreement and I accept full personal liability for my actions on this service."
                            : "Scroll to the bottom of the agreement to enable acceptance."}
                    </span>
                </label>
                <button
                    onClick={onContinue}
                    disabled={!accepted}
                    data-testid="waiver-accept-btn"
                    className="w-full h-14 bg-smash hover:bg-smash/90 disabled:bg-white/10 disabled:text-white/30 text-white font-display text-lg tracking-wider uppercase transition-colors"
                >
                    I Accept & Continue
                </button>
            </div>
        </div>
    );
}
